import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight } from "lucide-react";
import { LOGO_URL } from "../../lib/api";

const NAV = [
  { label: "HOME", to: "/" },
  { label: "DISPATCH", to: "/dispatch" },
  { label: "HR & BACK OFFICE", to: "/hr-back-office" },
  { label: "START A COMPANY", to: "/start-a-trucking-company" },
  { label: "DISPATCH CLASS", to: "/dispatch-class" },
  { label: "SAFETY AUDIT", to: "/safety-audit" },
  { label: "MENTORSHIP", to: "/elite-mentorship" },
  { label: "ABOUT AMANDA", to: "/about-amanda" },
  { label: "CONTACT", to: "/contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => { setOpen(false); }, [loc.pathname]);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-[100] transition-all duration-500 ${
        scrolled ? "glass border-b border-[#D4AF37]/15 py-2" : "bg-transparent py-4"
      }`}
      data-testid="navbar"
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="nav-logo">
          <img src={LOGO_URL} alt="Next Mile Compliance LLC" className="h-10 sm:h-12 w-auto object-contain" />
        </Link>

        <div className="hidden xl:flex items-center gap-4 2xl:gap-6">
          {NAV.map((n) => {
            const active = loc.pathname === n.to;
            return (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-link-${n.to === "/" ? "home" : n.to.replace(/\//g, "")}`}
                className={`relative text-[11px] 2xl:text-[12px] font-semibold tracking-wider transition-colors ${
                  active ? "text-[#D4AF37]" : "text-gray-300 hover:text-white"
                }`}
              >
                {n.label}
                {active && (
                  <motion.span layoutId="nav-underline" className="absolute -bottom-1.5 left-0 right-0 h-[2px] nm-gold-bg" />
                )}
              </Link>
            );
          })}
        </div>

        <div className="flex items-center gap-3">
          <Link
            to="/contact"
            className="hidden sm:inline-flex btn-sheen items-center gap-2 nm-gold-bg text-[#0B132B] font-bold text-[13px] px-5 py-2.5 rounded-full hover:scale-[1.03] transition-transform"
            data-testid="nav-get-started-btn"
          >
            GET STARTED <ArrowRight size={15} />
          </Link>
          <button
            className="xl:hidden text-white p-2"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
            data-testid="nav-mobile-toggle"
          >
            <AnimatePresence mode="wait">
              {open ? (
                <motion.span key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ opacity: 0 }}>
                  <X size={26} />
                </motion.span>
              ) : (
                <motion.span key="m" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ opacity: 0 }}>
                  <Menu size={26} />
                </motion.span>
              )}
            </AnimatePresence>
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            className="xl:hidden glass border-t border-[#D4AF37]/15 overflow-hidden"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35 }}
            data-testid="nav-mobile-menu"
          >
            <div className="px-5 py-4 flex flex-col">
              {NAV.map((n, i) => (
                <motion.div key={n.to} initial={{ x: -20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: i * 0.04 }}>
                  <Link to={n.to} className="block py-3 text-sm font-semibold tracking-wider text-gray-200 border-b border-white/5">
                    {n.label}
                  </Link>
                </motion.div>
              ))}
              <Link to="/contact" className="mt-4 text-center nm-gold-bg text-[#0B132B] font-bold py-3 rounded-full">
                GET STARTED
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
